import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MapPin, Shapes, Users } from 'lucide-react';
import {
  RadiusMapVisualization,
  type RadiusMapHandle,
} from '@/components/shared/RadiusMapVisualization';
import { useCompanyLocationMapCenter } from '@/hooks/useCompanyLocationMapCenter';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { useAppConfig } from '../../../contexts/AppConfigContext';
import { useTenantData } from '../../../contexts/TenantDataContext';
import type { Routemize_DTOs_CompanyLocationDto } from '../../../api/models/Routemize_DTOs_CompanyLocationDto';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import {
  loadWorkAreaContext,
  saveServiceRadius,
  type BaseLocationChoice,
} from '../../../business/onboarding/onboardingLocations';
import { Disclosure, StepIntro } from '../shared/StepShell';

const RADIUS_PRESETS = [15, 25, 35, 50, 75, 100];
const MIN_RADIUS = 1;
const MAX_RADIUS = 150;

export const RadiusStep: React.FC = () => {
  const { completeStep } = useOnboarding();
  const { config } = useAppConfig();
  const { country: tenantCountry } = useTenantData();
  const userId = config?.currentUser?.id;
  // US works in ZIP codes, Canada in FSA-level postal codes — matches the
  // wording used by Settings → Route Preferences.
  const areaLabel = tenantCountry === 'CA' ? 'postal codes' : 'zip codes';

  const [isLoading, setIsLoading] = useState(true);
  const [radius, setRadius] = useState(25);
  const [startLocation, setStartLocation] = useState<BaseLocationChoice>('home');
  const [mapLocation, setMapLocation] = useState<Routemize_DTOs_CompanyLocationDto | null>(null);

  const radiusMapRef = useRef<RadiusMapHandle>(null);

  useEffect(() => {
    let cancelled = false;

    loadWorkAreaContext(userId)
      .then(({ homeAddress, officeLocation, settings }) => {
        if (cancelled) return;

        const start =
          settings?.routePreferences?.defaultStartingLocation === 'office'
            ? 'office'
            : ('home' as BaseLocationChoice);
        setStartLocation(start);

        // The circle is drawn around wherever the day begins, so the radius
        // being set is the one that will actually be applied.
        if (start === 'office') {
          setMapLocation(officeLocation);
        } else if (homeAddress) {
          setMapLocation({
            addressLine1: homeAddress.addressLine1,
            city: homeAddress.city,
            state: homeAddress.state,
            zipCode: homeAddress.zipCode,
            country: homeAddress.country,
            latitude: homeAddress.latitude,
            longitude: homeAddress.longitude,
          });
        }

        if (settings?.maxTravelRadiusMiles && settings.maxTravelRadiusMiles > 0) {
          setRadius(settings.maxTravelRadiusMiles);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const { center, isLoading: isCenterLoading } = useCompanyLocationMapCenter(
    mapLocation,
    !isLoading
  );

  useStepValidity(radius >= MIN_RADIUS);

  const applyPreset = useCallback((value: number) => {
    radiusMapRef.current?.endUserEdit();
    setRadius(value);
  }, []);

  useStepSaver(
    ONBOARDING_STEP.RADIUS,
    useCallback(async () => {
      await saveServiceRadius(radius);
      completeStep(ONBOARDING_STEP.RADIUS);
    }, [radius, completeStep])
  );

  const driveEstimate = useMemo(() => Math.round((radius / 40) * 60), [radius]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StepIntro>
        {`The furthest you're willing to drive from your ${
          startLocation === 'office' ? 'office' : 'home'
        }. Requests outside it are flagged rather than blocked, so you can still take them.`}
      </StepIntro>

      {/* The number and the slider sit above the map so the control is the
          first thing in reach on a phone, with the map confirming it. */}
      <div className="overflow-hidden rounded-xl border border-ink-200 bg-surface">
        <div className="border-b border-ink-100 p-4 sm:p-5">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="text-3xl font-bold tabular-nums text-ink-900">
              {radius}
              <span className="ml-1 text-base font-semibold text-ink-500">miles</span>
            </span>
            <span className="text-xs text-ink-500">~{driveEstimate} min drive</span>
          </div>

          <input
            type="range"
            min={MIN_RADIUS}
            max={MAX_RADIUS}
            value={radius}
            onChange={e => setRadius(Number(e.target.value))}
            aria-label="Service radius in miles"
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-ink-200 accent-accent-600"
          />
          <div className="mt-1 flex justify-between text-2xs text-ink-400">
            <span>{MIN_RADIUS} mi</span>
            <span>{MAX_RADIUS} mi</span>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {RADIUS_PRESETS.map(preset => (
              // eslint-disable-next-line no-restricted-syntax -- compact chip in a preset row; Button's min-height is too tall here
              <button
                key={preset}
                type="button"
                onClick={() => applyPreset(preset)}
                aria-pressed={radius === preset}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  radius === preset
                    ? 'border-accent-500 bg-accent-50 text-accent-700'
                    : 'border-ink-200 bg-surface text-ink-600 hover:border-ink-300 hover:bg-ink-50'
                }`}
              >
                {preset} mi
              </button>
            ))}
          </div>
        </div>

        {center ? (
          <>
            <RadiusMapVisualization
              ref={radiusMapRef}
              radius={radius}
              startLocation={startLocation}
              coordinates={center}
              minRadiusMiles={MIN_RADIUS}
              maxRadiusMiles={MAX_RADIUS}
              onRadiusChange={setRadius}
              className="h-56 sm:h-72"
            />
            <p className="bg-ink-50 px-4 py-2 text-center text-xs text-ink-500">
              Drag the edge of the circle to fine-tune it.
            </p>
          </>
        ) : (
          <div className="flex h-56 flex-col items-center justify-center gap-2 bg-ink-50 px-4 text-center">
            {isCenterLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-ink-400" />
            ) : (
              <>
                <MapPin className="h-5 w-5 text-ink-300" />
                <p className="text-xs text-ink-500">
                  We couldn't place your starting address on the map, but your radius will still
                  be saved.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <Disclosure summary="A circle isn't your only option">
        <ul className="space-y-2.5">
          <li className="flex gap-2">
            <Shapes className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-ink-400" />
            <span className="text-xs leading-relaxed text-ink-600">
              Draw a <span className="font-medium text-ink-800">custom service area</span> on the
              map or build one from specific {areaLabel}, instead of a radius.
            </span>
          </li>
          <li className="flex gap-2">
            <Users className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-ink-400" />
            <span className="text-xs leading-relaxed text-ink-600">
              Split it into <span className="font-medium text-ink-800">territories</span> so each
              salesperson covers their own area, and appointments route to whoever owns that patch.
            </span>
          </li>
        </ul>
        <p className="mt-2.5 text-11 text-ink-400">
          Both live in Settings → Route Preferences. Start with a radius here — you can switch any
          time without redoing this step.
        </p>
      </Disclosure>
    </div>
  );
};
